import { isEmpty } from "lodash";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Dimmer, Grid, Loader } from "semantic-ui-react";
import { useGetEnergyType } from "../../../api/common";
import {
  Energy,
  HeatSource,
  RefrigerantSource,
  useEditFacility,
  useGetFacilityById,
  WaterSource
} from "../../../api/facility";
import {
  useGetHeatSourceType,
  useGetRefrigerantSourceType,
  useGetWaterSourceType
} from "../../../api/static-data";
import { CommonModal } from "../../../components";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import MainBottomMultipleButtonView from "../../../components/mainBottomButtonView/MainBottomMultipleButtonView";
import {
  DISABLE_FACILITY_SIMULATION,
  FACILITY_OWNERSHIP,
  FACILITY_TYPES
} from "../../../config/constants";
import { errorView } from "../../../helpers/ErrorHandler";
import "../facilityEdit/facilityEdit.scss";
import FacilityEnergySimulation from "../facilitySimulationForms/FacilityEnergySimulation";
import FacilityHeatSourceSimulation from "../facilitySimulationForms/FacilityHeatSourceSimulation";
import FacilityInfoSimulation from "../facilitySimulationForms/FacilityInfoSimulation";
import FacilityRefrigerantSimulation from "../facilitySimulationForms/FacilityRefrigerantSimulation";
import FacilityWaterSourceSimulation from "../facilitySimulationForms/FacilityWaterSourceSimulation";

interface FacilitySimulationViewModalProps {
  visibleModal: boolean;
  setVisibleModal: (visible: boolean) => void;
  facilityId: string | undefined;
  handleChangeView?: () => void;
}

const FacilitySimulationViewModal = ({
  visibleModal,
  setVisibleModal,
  facilityId,
  handleChangeView
}: FacilitySimulationViewModalProps) => {
  type EditFacilityDataType = {
    _id?: string;
    energy?: Energy[];
    heatSource?: HeatSource[];
    waterSource?: WaterSource[];
    refrigerantSource?: RefrigerantSource[];
    [key: string]: any;
  };

  const [editFacilityData, setEditFacilityData] = useState<EditFacilityDataType | undefined>();
  const [visibleDeleteModal, setVisibleDeleteModal] = useState<boolean>(false);
  const [visibleHeatDeleteModal, setVisibleHeatDeleteModal] = useState<boolean>(false);
  const [visibleRefrigerantDeleteModal, setVisibleRefrigerantDeleteModal] =
    useState<boolean>(false);
  const [visibleWaterDeleteModal, setVisibleWaterDeleteModal] = useState<boolean>(false);
  const [deleteData, setDeleteData] = useState<Energy | null>(null);
  const [deleteHeatData, setDeleteHeatData] = useState<HeatSource | null>(null);
  const [deleteRefrigerantData, setDeleteRefrigerantData] = useState<RefrigerantSource | null>(
    null
  );
  const [deleteWaterData, setDeleteWaterData] = useState<WaterSource | null>(null);
  const [defaultFacilityType, setDefaultType] = useState<string>(FACILITY_TYPES[0].value);
  const [defaultFacilityOwnerShip, setDefaultOwnerShip] = useState<string>(
    FACILITY_OWNERSHIP[0].value
  );
  const [country, setCountry] = useState<string>("");

  const { data: energyType, status, fetchStatus } = useGetEnergyType();
  const { data: facilityData, isFetching: isFacilityLoading } = useGetFacilityById(
    facilityId || ""
  );
  const { data: originalFacilityData, isFetching: isOriginalFacilityLoading } = useGetFacilityById(
    facilityData?.originalId || ""
  );

  const isSimulatedFacility = facilityData?.isSimulatedFacility ?? false;

  const { isLoading: editFacilityLoading, mutate: editFacilitySimulation } = useEditFacility();

  const deleteFacilitySource = <T,>(
    key: "energy" | "heatSource" | "refrigerantSource" | "waterSource",
    deleteItem: T | null,
    setDeleteItem: React.Dispatch<React.SetStateAction<T | null>>
  ) => {
    if (!deleteItem) return;

    const list = editFacilityData?.[key] || [];
    const indexToDelete = list.findIndex((item: any) => item === deleteItem);

    if (indexToDelete !== -1) {
      const updatedList = [...list.slice(0, indexToDelete), ...list.slice(indexToDelete + 1)];

      setEditFacilityData((prev: any) => ({
        ...prev,
        [key]: updatedList
      }));

      setDeleteItem(null);
    }
  };

  const deleteFacilityEnergy = () => {
    switch (true) {
      case visibleDeleteModal:
        deleteFacilitySource("energy", deleteData, setDeleteData);
        break;
      case visibleHeatDeleteModal:
        deleteFacilitySource("heatSource", deleteHeatData, setDeleteHeatData);
        break;
      case visibleRefrigerantDeleteModal:
        deleteFacilitySource("refrigerantSource", deleteRefrigerantData, setDeleteRefrigerantData);
        break;
      case visibleWaterDeleteModal:
        deleteFacilitySource("waterSource", deleteWaterData, setDeleteWaterData);
        break;
      default:
        break;
    }
  };

  const {
    data: heatSource,
    status: heatSourceStatus,
    fetchStatus: fetchHeatSource
  } = useGetHeatSourceType();

  const {
    data: waterSource,
    status: waterSourceStatus,
    fetchStatus: fetchWaterSource
  } = useGetWaterSourceType();

  const values = useMemo(() => {
    setDefaultType(facilityData?.facilityType ?? FACILITY_TYPES[0].value);
    setCountry(facilityData?.facilityCountry ?? "");
    setDefaultOwnerShip(facilityData?.facilityOwnership || FACILITY_OWNERSHIP[0].value);
    setEditFacilityData(facilityData);
    return {
      facilityName: facilityData?.facilityName,
      facilityAddress: facilityData?.facilityAddress,
      facilityCity: facilityData?.facilityCity,
      facilityCountry: facilityData?.facilityCountry
    };
  }, [facilityData, visibleModal]);

  const {
    register,
    reset,
    formState: { errors },
    getValues,
    setValue: setFacilityValue
  } = useForm({
    defaultValues: values,
    values: values
  });

  const {
    data: refrigerantSource,
    status: refrigerantStatus,
    fetchStatus: refrigerantFetchStatus
  } = useGetRefrigerantSourceType();

  const checkFacilitySaveCondition = (data: any) => {
    if (isEmpty(data.facilityName.trim())) {
      errorView("Facility name is required");
      return true;
    } else if (!country) {
      errorView("Facility country is required");
      return true;
    } else if ((facilityData?.energy ?? []).length <= 0) {
      errorView("Please add facility energy");
      return true;
    } else {
      return false;
    }
  };

  const onSubmit = async (data: any) => {
    if (!checkFacilitySaveCondition(data)) {
      const { facilityName, facilityAddress, facilityCity } = data;
      const facilityDetails = {
        _id: facilityData?._id,
        facilityName,
        facilityAddress,
        facilityCity,
        facilityCountry: country,
        facilityType: defaultFacilityType,
        facilityOwnership: defaultFacilityOwnerShip,
        energy: editFacilityData?.energy ?? [],
        heatSource: editFacilityData?.heatSource ?? [],
        waterSource: editFacilityData?.waterSource ?? [],
        refrigerantSource: editFacilityData?.refrigerantSource ?? [],
        originalId: isSimulatedFacility ? facilityData?.originalId : undefined
      };
      editFacilitySimulation(
        { data: facilityDetails, simulatedFacility: isSimulatedFacility },
        {
          onSuccess: () => {
            setVisibleModal(true);
            reset();
          }
        }
      );
      if (!data?.enableNavigate || false) {
        setVisibleModal(true);
      }
    }
  };

  const saveFacilityData = () => {
    const values = getValues();
    onSubmit(values);
  };

  const handleCancelDelete = () => {
    setVisibleDeleteModal(false);
    setDeleteData(null);
    setVisibleHeatDeleteModal(false);
    setDeleteHeatData(null);
    setVisibleWaterDeleteModal(false);
    setDeleteWaterData(null);
    setVisibleRefrigerantDeleteModal(false);
    setDeleteRefrigerantData(null);
  };

  const handleApproveDelete = () => {
    setVisibleDeleteModal(false);
    setVisibleHeatDeleteModal(false);
    setVisibleWaterDeleteModal(false);
    setVisibleRefrigerantDeleteModal(false);
    deleteFacilityEnergy();
  };

  if (
    (status == "loading" && fetchStatus == "fetching") ||
    (heatSourceStatus == "loading" && fetchHeatSource == "fetching") ||
    (waterSourceStatus == "loading" && fetchWaterSource == "fetching") ||
    (refrigerantStatus == "loading" && refrigerantFetchStatus == "fetching") ||
    isOriginalFacilityLoading ||
    isFacilityLoading ||
    editFacilityLoading
  ) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      <CommonModal
        modalOpenState={visibleModal}
        modalCloseState={() => {
          setVisibleModal(false);
        }}
        centered={true}
        title={"Facility Simulation"}
        customDimmer={isSimulatedFacility ? "custom-dimmer-modal-simulation" : ""}
      >
        <Grid>
          <Grid.Column computer={16} tablet={16} mobile={16}>
            <CommonSplitContainer
              currentHeight={0.91}
              topChild={
                <>
                  <FacilityInfoSimulation
                    register={register}
                    errors={errors}
                    country={country}
                    setFacilityValue={setFacilityValue}
                    setCountry={setCountry}
                    defaultFacilityType={defaultFacilityType}
                    setDefaultType={setDefaultType}
                    defaultFacilityOwnerShip={defaultFacilityOwnerShip}
                    setDefaultOwnerShip={setDefaultOwnerShip}
                    facilityData={editFacilityData}
                    originalFacilityData={originalFacilityData}
                    isFacilitySimulation={isSimulatedFacility}
                    isViewMode={true}
                  />
                  <FacilityEnergySimulation
                    energyType={energyType}
                    currentFacilityData={editFacilityData?.energy || []}
                    originalFacilityEnergyData={originalFacilityData?.energy || []}
                    setDeleteData={setDeleteData}
                    setVisibleDeleteModal={setVisibleDeleteModal}
                    isFacilitySimulation={isSimulatedFacility}
                    isViewMode={true}
                  />
                  <FacilityHeatSourceSimulation
                    heatType={heatSource}
                    currentFacilityHeatData={editFacilityData?.heatSource || []}
                    originalHeatSourceData={originalFacilityData?.heatSource || []}
                    setDeleteHeatData={setDeleteHeatData}
                    setVisibleDeleteHeatModal={setVisibleHeatDeleteModal}
                    isFacilitySimulation={isSimulatedFacility}
                    isViewMode={true}
                  />
                  <FacilityWaterSourceSimulation
                    waterType={waterSource}
                    currentFacilityWaterData={editFacilityData?.waterSource || []}
                    originalWaterSourceData={originalFacilityData?.waterSource || []}
                    setDeleteWaterData={setDeleteWaterData}
                    setVisibleDeleteWaterModal={setVisibleWaterDeleteModal}
                    isFacilitySimulation={isSimulatedFacility}
                    isViewMode={true}
                  />
                  <FacilityRefrigerantSimulation
                    refrigerantType={refrigerantSource}
                    currentFacilityRefrigerantData={editFacilityData?.refrigerantSource || []}
                    originalRefrigerantSourceData={originalFacilityData?.refrigerantSource || []}
                    setDeleteRefrigerantData={setDeleteRefrigerantData}
                    setVisibleDeleteRefrigerantModal={setVisibleRefrigerantDeleteModal}
                    isFacilitySimulation={isSimulatedFacility}
                    isViewMode={true}
                  />
                </>
              }
              bottomChild={
                <MainBottomMultipleButtonView
                  leftButtons={[
                    {
                      title: "Cancel",
                      onClick: () => {
                        setVisibleModal(false);
                      },
                      buttonOutLine: true,
                      buttonView: true
                    }
                  ]}
                  rightButtons={[
                    {
                      contentView: 16,
                      title: "Edit",
                      onClick: () => handleChangeView?.(),
                      buttonView: !DISABLE_FACILITY_SIMULATION
                    }
                  ]}
                />
              }
            />
          </Grid.Column>
        </Grid>
      </CommonModal>
      <ConfirmModal
        viewModal={
          visibleDeleteModal ||
          visibleHeatDeleteModal ||
          visibleWaterDeleteModal ||
          visibleRefrigerantDeleteModal
        }
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => {
          handleCancelDelete();
        }}
        approve={() => {
          handleApproveDelete();
        }}
        title="Delete Facility Energy"
        subTitle="Are you sure you want to delete facility energy?"
      />
    </>
  );
};

export default FacilitySimulationViewModal;
